"use client";

import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { COPY } from "../../lib/copy";
import {
  CONVERSATION_STATES,
  reasonLabelFor,
  stateLabel,
  type ConversationState,
  type Tone,
} from "../../lib/labels";
import { showsWhy } from "../shared/why-skipped";

const SPINNING = new Set(["joining", "await_member", "await_consent", "processing"]);

export function formatCaptureWhen(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const TONE_ICON: Record<Tone, string> = {
  neutral: "text-contrast/55",
  info: "text-heading",
  success: "text-heading",
  warning: "text-warning",
  danger: "text-destructive",
};

function knownStatusBody(state: ConversationState, when: string | null): string | null {
  const lines = COPY.media.captureStatus;
  switch (state) {
    case "ready":
      return null;
    case "scheduled":
      return when ? lines.scheduled(formatCaptureWhen(when) ?? when) : lines.scheduledOpen;
    case "joining":
      return lines.joining;
    case "await_member":
      return lines.await_member;
    case "await_consent":
      return lines.await_consent;
    case "capturing":
      return lines.capturing;
    case "paused":
      return lines.paused;
    case "consent_hold":
      return lines.consent_hold;
    case "member_hold":
      return lines.member_hold;
    case "processing":
      return lines.processing;
    case "skipped":
      return lines.skipped;
    case "declined":
      return lines.declined;
    case "blocked":
      return lines.blocked;
    case "cancelled":
      return lines.cancelled;
    case "failed":
      return lines.failed;
    case "empty":
      return lines.empty;
    case "discarded":
      return lines.discarded;
    case "no_show":
      return lines.no_show;
    case "revoked":
      return lines.revoked;
    case "consent_withdrawn":
      return lines.consent_withdrawn;
    default: {
      const uncovered: never = state;
      return uncovered;
    }
  }
}

export function captureStatusBody(
  state: string,
  when: string | null,
  reason?: string | null,
): string | null {
  if (!(CONVERSATION_STATES as readonly string[]).includes(state)) return null;
  const generic = knownStatusBody(state as ConversationState, when);
  if ((showsWhy(state) || state === "revoked" || state === "consent_withdrawn") && reason) {
    return reasonLabelFor(state, reason) || generic;
  }
  return generic;
}

/** Replaces the player and the transcript skeleton while there is nothing to play. */
export function CaptureStatus({
  state,
  when,
  reason = null,
  className,
}: {
  state: string;
  when: string | null;
  reason?: string | null;
  className?: string;
}) {
  const meta = stateLabel(state);
  const spinning = SPINNING.has(state);
  return (
    <div role="status" className={cn("flex items-start gap-2.5 px-1 py-3", className)}>
      <Icon
        name={meta.icon}
        size={16}
        aria-hidden
        className={cn("mt-0.5 shrink-0", TONE_ICON[meta.tone], spinning && "animate-spin")}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-sm font-medium text-heading">{meta.label}</p>
        <p className="text-sm text-contrast/60">
          {captureStatusBody(state, when, reason) ?? COPY.media.captureStatus.unknown}
        </p>
      </div>
    </div>
  );
}
